/**
   * Deep equality check for objects without using lodash
   * @param obj1 First object to compare
   * @param obj2 Second object to compare
   * @returns true if objects are deeply equal
   */
export default function deepEqual<T>(obj1: T, obj2: T): boolean {
  // If both are primitive types or null/undefined
  if (obj1 === obj2) return true;

  // If one is null/undefined but not the other
  if (obj1 == null || obj2 == null) return false;

  // If they are not both objects
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return false;

  // If they are arrays
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) return false;

    for (let i = 0; i < obj1.length; i++) {
      if (!deepEqual(obj1[i], obj2[i])) return false;
    }

    return true;
  }

  // If one is array and other is not
  if (Array.isArray(obj1) !== Array.isArray(obj2)) return false;

  // Both are objects, compare their properties
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  return keys1.every(key =>
    Object.prototype.hasOwnProperty.call(obj2, key)
    && deepEqual(obj1[key as keyof T], obj2[key as keyof T]),
  );
}
